import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    number: '01',
    label: 'Audit',
    icon: 'ri-search-eye-line',
    description: 'We analyze your existing workflows, tools, and bottlenecks to identify the highest-impact automation opportunities.',
    color: '#7C3AED',
  },
  {
    number: '02',
    label: 'Design',
    icon: 'ri-layout-4-line',
    description: 'We map a custom system architecture — defining triggers, logic paths, integrations, and automation rules.',
    color: '#9333EA',
  },
  {
    number: '03',
    label: 'Build',
    icon: 'ri-tools-line',
    description: 'Our team configures, connects, and builds every component of your automation system from the ground up.',
    color: '#14B8A6',
  },
  {
    number: '04',
    label: 'Deploy',
    icon: 'ri-rocket-2-line',
    description: 'We launch your system with testing, team training, and a structured rollout to ensure smooth adoption.',
    color: '#0D9488',
  },
  {
    number: '05',
    label: 'Optimize',
    icon: 'ri-line-chart-line',
    description: 'We monitor performance, refine logic, and continuously improve your system as your business grows.',
    color: '#7C3AED',
  },
];

const ProcessSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="process"
      className="relative py-28 overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #0f0e2e 0%, #1E1B4B 50%, #1a1740 100%)' }}
    >
      {/* Background accents */}
      <div className="absolute top-1/2 left-1/4 w-64 h-64 rounded-full bg-brand-violet/8 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/3 w-48 h-48 rounded-full bg-brand-teal/8 blur-3xl pointer-events-none" />

      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          className="text-center max-w-xl mx-auto mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="font-inter text-xs font-semibold text-brand-teal tracking-widest uppercase mb-4 block">
            Our Process
          </span>
          <h2
            className="font-space font-semibold text-white leading-tight mb-5"
            style={{ fontSize: 'clamp(2rem, 3.5vw, 2.75rem)' }}
          >
            How We Build Your System
          </h2>
          <p className="font-inter text-white/55 text-base leading-relaxed">
            A structured, transparent process that takes you from disorganized operations to a fully automated business system — in weeks, not months.
          </p>
        </div>

        {/* Desktop: horizontal timeline */}
        <div className="hidden lg:block">
          {/* Progress line */}
          <div className="relative mb-12">
            <div className="absolute top-8 left-[10%] right-[10%] h-px bg-white/10" />
            <div
              className="absolute top-8 left-[10%] h-px bg-gradient-to-r from-brand-violet to-brand-teal transition-all duration-1000"
              style={{ right: visible ? '10%' : '90%' }}
            />

            {/* Step circles */}
            <div className="flex justify-between px-[10%]">
              {steps.map((step, i) => (
                <div key={step.label} className="flex flex-col items-center gap-0">
                  {/* Circle */}
                  <div
                    className="relative z-10 w-16 h-16 rounded-full flex items-center justify-center border-2 border-white/20 transition-all duration-300"
                    style={{
                      background: `linear-gradient(135deg, ${step.color}40, ${step.color}20)`,
                      borderColor: `${step.color}50`,
                      opacity: visible ? 1 : 0,
                      transform: visible ? 'scale(1)' : 'scale(0.7)',
                      transition: `opacity 0.5s ease ${0.2 + i * 0.15}s, transform 0.5s ease ${0.2 + i * 0.15}s`,
                    }}
                  >
                    <i className={`${step.icon} text-lg`} style={{ color: step.color }} />
                  </div>
                  {/* Number badge */}
                  <span
                    className="font-inter text-[10px] font-bold mt-2 tracking-widest"
                    style={{ color: `${step.color}80` }}
                  >
                    {step.number}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Step cards */}
          <div className="grid grid-cols-5 gap-5">
            {steps.map((step, i) => (
              <div
                key={step.label}
                className="bg-white/5 rounded-xl p-5 border border-white/8 hover:border-white/15 transition-all duration-300 cursor-default"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(24px)',
                  transition: `opacity 0.6s ease ${0.3 + i * 0.12}s, transform 0.6s ease ${0.3 + i * 0.12}s`,
                }}
              >
                <h3 className="font-space font-semibold text-white text-base mb-3">{step.label}</h3>
                <p className="font-inter text-white/50 text-xs leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile: vertical timeline */}
        <div className="lg:hidden flex flex-col gap-0">
          {steps.map((step, i) => (
            <div key={step.label} className="flex gap-5">
              {/* Left: line + circle */}
              <div className="flex flex-col items-center">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 border"
                  style={{
                    background: `${step.color}20`,
                    borderColor: `${step.color}40`,
                    opacity: visible ? 1 : 0,
                    transition: `opacity 0.5s ease ${0.2 + i * 0.12}s`,
                  }}
                >
                  <i className={`${step.icon} text-base`} style={{ color: step.color }} />
                </div>
                {i < steps.length - 1 && (
                  <div className="w-px flex-1 bg-white/10 my-2" style={{ minHeight: '2rem' }} />
                )}
              </div>
              {/* Right: content */}
              <div
                className="pb-8"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateX(0)' : 'translateX(16px)',
                  transition: `opacity 0.6s ease ${0.2 + i * 0.12}s, transform 0.6s ease ${0.2 + i * 0.12}s`,
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="font-inter text-[10px] font-bold tracking-widest"
                    style={{ color: `${step.color}80` }}
                  >
                    {step.number}
                  </span>
                  <h3 className="font-space font-semibold text-white text-sm">{step.label}</h3>
                </div>
                <p className="font-inter text-white/50 text-xs leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className="mt-16 text-center"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.9s',
          }}
        >
          <p className="font-inter text-white/45 text-sm mb-5">
            Most clients see results within the first 4–6 weeks of deployment.
          </p>
          <button
            onClick={() => document.querySelector('#industries')?.scrollIntoView({ behavior: 'smooth' })}
            className="whitespace-nowrap font-inter font-semibold text-sm text-brand-indigo px-7 py-3.5 rounded-lg bg-brand-teal hover:opacity-90 transition-opacity cursor-pointer inline-flex items-center gap-2"
          >
            See Who We Work With
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
